import { Link } from "@tvg/fuse/link";
import classNames from "classnames";
import React, { FC, useEffect, useRef, useMemo } from "react";
import { useSubmenuKeyboardNav } from "../hooks/useSubmenuKeyboardNav";
import styles from "./Submenu.module.css";
import {
  renderFewCategories,
  renderSevenPlusCategories,
  renderSingleCategory,
} from "./SubmenuRenders";

interface Item {
  name: string;
  link: string;
}
interface Split {
  name: string;
  items: Item[];
}
interface SubmenuProps {
  splits: Split[];
  departmentUrl: string;
  departmentName: string;
  ariaLabelledBy: string;
  id: string;
  ariaHidden: boolean;
  showSubmenu: boolean;
  onSubmenuEnter: () => void;
  onSubmenuLeave: () => void;
  parentButtonRef: React.RefObject<HTMLElement>;
}

const SubmenuOptimized: FC<SubmenuProps> = ({
  splits,
  departmentUrl,
  departmentName,
  id,
  ariaHidden,
  showSubmenu,
  onSubmenuLeave,
  parentButtonRef,
  ariaLabelledBy,
}) => {
  const submenuRef = useRef<HTMLDivElement>(null);

  const allDepartmentsLink = useMemo(
    () => (
      <div className={styles.submenu__allDepartmentsLinkWrapper}>
        <Link
          className={styles.submenu__allDepartmentsLink}
          href={departmentUrl}
          chevron
          iconRight
        >
          {`See all ${departmentName}`}
        </Link>
      </div>
    ),
    [departmentUrl, departmentName],
  );

  useSubmenuKeyboardNav({
    submenuRef,
    showSubmenu,
    onClose: onSubmenuLeave,
    parentButtonRef,
  });

  useEffect(() => {
    if (showSubmenu && submenuRef.current) {
      submenuRef.current.scrollTop = 0;
    }
  }, [showSubmenu]);

  return (
    <div
      className={classNames(`${styles.submenu__nav} submenu__nav`, {
        [styles["submenu__nav--open"]]: showSubmenu,
      })}
      ref={submenuRef}
      data-testid="submenu-nav-container"
      id={id}
      aria-hidden={ariaHidden}
      aria-labelledby={ariaLabelledBy}
      hidden={ariaHidden}
    >
      <div
        className={`${styles.submenu__gridContainer} submenu__gridContainer`}
      >
        {splits.length === 1
          ? renderSingleCategory(splits[0])
          : splits.length <= 6
            ? renderFewCategories(splits)
            : renderSevenPlusCategories(splits)}

        {allDepartmentsLink}
      </div>
    </div>
  );
};

export default React.memo(SubmenuOptimized, (prev, next) => {
  return (
    prev.showSubmenu === next.showSubmenu &&
    prev.ariaHidden === next.ariaHidden &&
    prev.departmentUrl === next.departmentUrl &&
    prev.departmentName === next.departmentName
  );
});
